<template>
  <div class="ai" :class="[$theme?.baseTextColor]">
    <div class="panel" role="status" aria-live="polite">
      <!-- Фаза -->
      <div class="phaseChip">
        <span class="dot"></span>
        {{ currentGroup.name }}
      </div>

      <!-- Печатающаяся строка -->
      <div class="line" :key="groupIndex + '-' + msgIndex">
        <span>{{ typedText }}</span><span class="caret" :class="{ blink: !isTyping }">▋</span>
      </div>

      <!-- Прогресс по текущей строке (div, без SVG) -->
      <div class="tokenProgress" aria-hidden="true">
        <div class="tokenProgress__bar" :style="{ width: Math.round(progress * 100) + '%' }"></div>
      </div>

      <!-- Лента «мыслей» -->
      <transition-group name="fade-slide" tag="ul" class="thoughts" aria-hidden="true">
        <li v-for="t in thoughts" :key="t.id" class="thoughtLine">
          <span class="tag">[{{ currentGroup.key }}]</span> {{ t.text }}
        </li>
      </transition-group>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AiPlaceholder',
  data() {
    return {
      // Параметры печати
      typeSpeed: 26,   // мс/символ
      holdMs: 1200,    // пауза после строки

      // Состояния печати
      isTyping: true,
      charIndex: 0,
      groupIndex: 0,
      msgIndex: 0,
      typeTimer: null,
      holdTimer: null,

      // Рандомизация
      randomWalk: true, // true = следующая строка/фаза выбираются случайно

      // «Мысли»
      thoughts: [],
      thinkTimer: null,
      thoughtSeq: 0,
      maxThoughts: 6,

      // Группы
      groups: [
        {
          key: 'calibrate',
          name: this.$t('gr_name1'),
          list: [
            this.$t('gr1_item1'),
            this.$t('gr1_item2'),
            this.$t('gr1_item3'),
            this.$t('gr1_item4'),
            this.$t('gr1_item5'),
            this.$t('gr1_item6'),
            this.$t('gr1_item7'),
            this.$t('gr1_item8'),
            this.$t('gr1_item9'),
            this.$t('gr1_item10'),
            this.$t('gr1_item11'),
            this.$t('gr1_item12'),
            this.$t('gr1_item13'),
            this.$t('gr1_item14'),
            this.$t('gr1_item15')
          ]
        },
        {
          key: 'search',
          name: this.$t('gr_name2'),
          list: [
            this.$t('gr2_item1'),
            this.$t('gr2_item2'),
            this.$t('gr2_item3'),
            this.$t('gr2_item4'),
            this.$t('gr2_item5'),
            this.$t('gr2_item6'),
            this.$t('gr2_item7'),
            this.$t('gr2_item8'),
            this.$t('gr2_item9'),
            this.$t('gr2_item10'),
            this.$t('gr2_item11'),
            this.$t('gr2_item12'),
          ]
        },
        {
          key: 'risk',
          name: this.$t('gr_name3'),
          list: [
            this.$t('gr3_item1'),
            this.$t('gr3_item2'),
            this.$t('gr3_item3'),
            this.$t('gr3_item4'),
            this.$t('gr3_item5'),
            this.$t('gr3_item6'),
            this.$t('gr3_item7'),
            this.$t('gr3_item8'),
            this.$t('gr3_item9'),
            this.$t('gr3_item10'),
          ]
        },
        {
          key: 'ai',
          name: this.$t('gr_name4'),
          list: [
            this.$t('gr4_item1'),
            this.$t('gr4_item2'),
            this.$t('gr4_item3'),
            this.$t('gr4_item4'),
            this.$t('gr4_item5'),
            this.$t('gr4_item6'),
            this.$t('gr4_item7'),
            this.$t('gr4_item8'),
          ]
        }
      ]
    }
  },
  computed: {
    currentGroup() {
      return this.groups[this.groupIndex % this.groups.length]
    },
    currentMessage() {
      return this.currentGroup.list[this.msgIndex % this.currentGroup.list.length] || ''
    },
    typedText() {
      return this.currentMessage.slice(0, this.charIndex)
    },
    progress() {
      const len = Math.max(this.currentMessage.length, 1)
      return Math.min(this.charIndex / len, 1)
    }
  },
  mounted() {
    this.randomizeStartPoint()
    this.startTyping()
    this.startThinking()
  },
  beforeUnmount() {
    clearInterval(this.typeTimer)
    clearTimeout(this.holdTimer)
    clearTimeout(this.thinkTimer)
  },
  watch: {
    groupIndex() {
      this.thoughts = []
    },
    isTyping() {
      this.restartThinking()
    }
  },
  methods: {
    // ==== Рандом ====
    randInt(min, max) {
      return Math.floor(Math.random() * (max - min + 1)) + min
    },
    randomizeStartPoint() {
      this.groupIndex = this.randInt(0, this.groups.length - 1)
      const listLen = this.groups[this.groupIndex].list.length
      this.msgIndex = this.randInt(0, Math.max(0, listLen - 1))
      this.charIndex = 0
    },

    // ==== Печать ====
    startTyping() {
      this.isTyping = true
      clearInterval(this.typeTimer)
      this.typeTimer = setInterval(() => {
        if (this.charIndex < this.currentMessage.length) {
          this.charIndex++
        } else {
          this.isTyping = false
          clearInterval(this.typeTimer)
          this.holdTimer = setTimeout(() => {
            this.nextMessage()
          }, this.holdMs)
        }
      }, this.typeSpeed)
    },
    nextMessage() {
      this.charIndex = 0

      if (this.randomWalk) {
        // Полностью случайный следующий выбор (без мгновенного повтора)
        const prevG = this.groupIndex
        const prevM = this.msgIndex

        this.groupIndex = this.randInt(0, this.groups.length - 1)
        const listLen = this.groups[this.groupIndex].list.length
        this.msgIndex = this.randInt(0, Math.max(0, listLen - 1))

        if (this.groupIndex === prevG && this.msgIndex === prevM && listLen > 1) {
          this.msgIndex = (this.msgIndex + 1) % listLen
        }
      } else {
        // Последовательный обход списков, старт уже был рандомный
        if (this.msgIndex < this.currentGroup.list.length - 1) {
          this.msgIndex++
        } else {
          this.msgIndex = 0
          this.groupIndex = (this.groupIndex + 1) % this.groups.length
        }
      }

      this.startTyping()
    },

    // ==== «Мысли» ====
    startThinking() {
      this.scheduleThought()
    },
    restartThinking() {
      clearTimeout(this.thinkTimer)
      this.scheduleThought()
    },
    scheduleThought() {
      const delay = this.isTyping ? 220 : 420
      this.thinkTimer = setTimeout(() => {
        this.pushThought()
        this.scheduleThought()
      }, delay)
    },
    pushThought() {
      const pool = this.currentGroup.list.filter(s => s && s !== this.currentMessage)
      if (!pool.length) return
      const raw = pool[Math.floor(Math.random() * pool.length)]
      const text = this.formatThought(raw)
      const id = ++this.thoughtSeq
      this.thoughts.push({ id, text })
      if (this.thoughts.length > this.maxThoughts) this.thoughts.shift()
    },
    formatThought(s) {
      const trimmed = String(s).replace(/\s+/g, ' ').trim()
      const max = 58
      const body = trimmed.length > max ? trimmed.slice(0, max - 1) + '…' : trimmed
      const markers = ['→', '∙', '·', '↦', '⟶']
      const m = markers[Math.floor(Math.random() * markers.length)]
      return `${m} ${body}`
    }
  }
}
</script>

<style scoped>
.ai { position: relative; width: 100%; }
.panel {
  max-width: 520px;
  margin: 0 auto;
  padding: 14px 16px 12px;
  border-radius: 16px;
  border: 1px solid rgba(240,121,56,.18);
  background:
      linear-gradient(180deg, rgba(240,121,56,.06), rgba(240,121,56,.03));
  backdrop-filter: blur(2px);
}

/* Фаза */
.phaseChip {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 4px 10px; border-radius: 999px;
  background: rgba(240,121,56,.09);
  border: 1px solid rgba(240,121,56,.25);
  font-size: 12px; font-weight: 700; letter-spacing: .1px;
  margin-bottom: 8px;
}
.phaseChip .dot {
  width: 8px; height: 8px; border-radius: 999px; display: inline-block;
  background: #f07938;
  box-shadow: 0 0 8px rgba(240,121,56,.5);
}

/* Печать */
.line { max-width: 100%; min-height: 2.2em; }
.caret { display: inline-block; margin-left: 2px; opacity: .85; }
.caret.blink { animation: blink 1.05s steps(1) infinite; }
@keyframes blink { 50% { opacity: 0; } }

/* Мини-прогресс */
.tokenProgress {
  position: relative;
  height: 4px;
  border-radius: 999px;
  background: rgba(240,121,56,.12);
  overflow: hidden;
  margin: 6px 0 2px;
}
.tokenProgress__bar {
  height: 100%;
  width: 0%;
  background: #f07938;
  opacity: .85;
  transition: width .12s linear;
}

/* Лента мыслей */
.thoughts {
  list-style: none;
  padding: 0; margin: 6px 0 0 0;
  width: 100%;
  display: grid; gap: 4px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
  font-weight: 500;
}
.thoughtLine {
  font-size: 12px;
  line-height: 1.25;
  opacity: .70;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.thoughtLine .tag {
  opacity: .55;
  margin-right: 6px;
  font-weight: 600;
}

/* Анимации появления */
.fade-slide-enter-active,
.fade-slide-leave-active { transition: all .28s ease; }
.fade-slide-enter-from { opacity: 0; transform: translateY(6px); }
.fade-slide-leave-to   { opacity: 0; transform: translateY(-6px); }

/* Адаптив */
@media (max-width: 600px) {
  .panel { padding: 12px; border-radius: 14px; }
  .phaseChip { font-size: 11px; }
  .thoughtLine { font-size: 11px; }
}
</style>
